import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiCheck, FiEdit2, FiPlus, FiSave, FiX } from "react-icons/fi";
import axios from "../../api/axiosInstance";

const emptyDraft = {
  id: null,
  code: "",
  name: "",
  symbol: "",
  minor_units: 2,
  sort_order: 0,
  is_active: true,
};

const normalizeCode = (value) => String(value || "").trim().toUpperCase();

export default function CurrenciesAdmin() {
  const [rows, setRows] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [selectedBusinessUnitId, setSelectedBusinessUnitId] = useState("");
  const [allowedCodes, setAllowedCodes] = useState([]);
  const [multiCurrencyEnabled, setMultiCurrencyEnabled] = useState(false);
  const [assignmentMeta, setAssignmentMeta] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assignmentSaving, setAssignmentSaving] = useState(false);
  const [error, setError] = useState("");
  const [assignmentMessage, setAssignmentMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [currenciesResponse, businessUnitsResponse] = await Promise.all([
        axios.get("/api/currencies/all"),
        axios.get("/api/business-units/all"),
      ]);
      const nextRows = Array.isArray(currenciesResponse.data) ? currenciesResponse.data : [];
      const nextBusinessUnits = Array.isArray(businessUnitsResponse.data)
        ? businessUnitsResponse.data
        : [];
      setRows(nextRows);
      setBusinessUnits(nextBusinessUnits);
      setSelectedBusinessUnitId((current) => current || String(nextBusinessUnits[0]?.id || ""));
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "Could not load currencies.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAssignment = useCallback(async () => {
    if (!selectedBusinessUnitId) {
      setAllowedCodes([]);
      setMultiCurrencyEnabled(false);
      setAssignmentMeta(null);
      return;
    }
    setAssignmentLoading(true);
    setAssignmentMessage(null);
    try {
      const response = await axios.get(
        `/api/currencies/business-units/${selectedBusinessUnitId}/allowed`
      );
      const data = response.data || {};
      setAllowedCodes(
        Array.isArray(data.allowed_currency_codes)
          ? data.allowed_currency_codes.map(normalizeCode)
          : []
      );
      setMultiCurrencyEnabled(Boolean(data.multi_currency_enabled));
      setAssignmentMeta(data);
    } catch (requestError) {
      setAllowedCodes([]);
      setMultiCurrencyEnabled(false);
      setAssignmentMeta(null);
      setAssignmentMessage({
        type: "error",
        text:
          requestError?.response?.data?.error ||
          "Could not load the Business Unit currency assignment.",
      });
    } finally {
      setAssignmentLoading(false);
    }
  }, [selectedBusinessUnitId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadAssignment();
  }, [loadAssignment]);

  const saveCatalogCurrency = async () => {
    const minorUnits = Number(draft?.minor_units);
    if (!Number.isInteger(minorUnits) || minorUnits < 0 || minorUnits > 6) {
      setError("Minor units must be a whole number between 0 and 6.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = { ...draft, minor_units: minorUnits };
      if (draft.id) await axios.put(`/api/currencies/${draft.id}`, payload);
      else await axios.post("/api/currencies", payload);
      setDraft(null);
      await load();
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "Could not save currency.");
    } finally {
      setSaving(false);
    }
  };

  const toggleAllowedCurrency = (code) => {
    const normalized = normalizeCode(code);
    setAllowedCodes((current) =>
      current.includes(normalized)
        ? current.filter((item) => item !== normalized)
        : [...current, normalized]
    );
    setAssignmentMessage(null);
  };

  const saveAssignment = async () => {
    if (!selectedBusinessUnitId) return;
    if (multiCurrencyEnabled && allowedCodes.length < 2) {
      setAssignmentMessage({
        type: "error",
        text: "Assign at least two currencies before enabling multi-currency for this Business Unit.",
      });
      return;
    }
    setAssignmentSaving(true);
    setAssignmentMessage(null);
    try {
      const response = await axios.put(
        `/api/currencies/business-units/${selectedBusinessUnitId}/allowed`,
        {
          currency_codes: allowedCodes,
          multi_currency_enabled: multiCurrencyEnabled,
        }
      );
      const data = response.data || {};
      setAllowedCodes(
        Array.isArray(data.allowed_currency_codes)
          ? data.allowed_currency_codes.map(normalizeCode)
          : []
      );
      setMultiCurrencyEnabled(Boolean(data.multi_currency_enabled));
      setAssignmentMeta(data);
      window.dispatchEvent(
        new CustomEvent("pilcore-currency-settings-updated", {
          detail: { business_unit_id: Number(selectedBusinessUnitId) },
        })
      );
      setAssignmentMessage({
        type: "success",
        text: data.multi_currency_enabled
          ? "Multi-currency enabled for this Business Unit. The hotel can now select currencies and maintain exchange rates in PMS Settings."
          : "Multi-currency disabled for this Business Unit. Existing reservation and exchange-rate history remains unchanged.",
      });
    } catch (requestError) {
      setAssignmentMessage({
        type: "error",
        text:
          requestError?.response?.data?.error ||
          "Could not save the Business Unit currency assignment.",
      });
    } finally {
      setAssignmentSaving(false);
    }
  };

  const activeCatalogRows = useMemo(
    () => rows.filter((row) => row.is_active !== false),
    [rows]
  );
  const enabledCodes = useMemo(
    () =>
      Array.isArray(assignmentMeta?.enabled_currency_codes)
        ? assignmentMeta.enabled_currency_codes.map(normalizeCode)
        : [],
    [assignmentMeta]
  );
  const baseCurrencyCode = normalizeCode(assignmentMeta?.base_currency_code);

  return (
    <div className="adminPage">
      <style>{`
        .currencyAdmin{padding:24px;color:#ececf7;display:grid;gap:20px}.currencyAdmin__head{display:flex;justify-content:space-between;gap:16px;align-items:flex-start}.currencyAdmin h1{font-size:1.5rem;margin:0 0 5px}.currencyAdmin h2{font-size:1.08rem;margin:0 0 6px}.currencyAdmin p{color:rgba(230,230,247,.62);margin:0}.currencyAdmin__card{padding:20px;border-radius:16px;border:1px solid rgba(255,255,255,.1);background:rgba(255,255,255,.035)}.currencyAdmin__cardHead{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;margin-bottom:16px}.currencyAdmin__button{display:inline-flex;align-items:center;justify-content:center;gap:8px;border:1px solid rgba(255,255,255,.14);background:#172038;color:#fff;border-radius:10px;padding:9px 13px;font-weight:800}.currencyAdmin__button--primary{background:linear-gradient(135deg,#20bba0,#2c82b8);border-color:transparent}.currencyAdmin__button:disabled{opacity:.55;cursor:not-allowed}.currencyAdmin__table{width:100%;border-collapse:separate;border-spacing:0 7px}.currencyAdmin__table th{padding:8px 12px;color:rgba(230,230,247,.55);font-size:.72rem;text-transform:uppercase;letter-spacing:.09em}.currencyAdmin__table td{padding:12px;background:rgba(255,255,255,.045);border-top:1px solid rgba(255,255,255,.07);border-bottom:1px solid rgba(255,255,255,.07)}.currencyAdmin__table td:first-child{border-left:1px solid rgba(255,255,255,.07);border-radius:12px 0 0 12px}.currencyAdmin__table td:last-child{border-right:1px solid rgba(255,255,255,.07);border-radius:0 12px 12px 0}.currencyAdmin__status{font-weight:800;color:#72e1ca}.currencyAdmin__status--off{color:#ef9aa5}.currencyAdmin__error,.currencyAdmin__message{padding:11px 13px;border-radius:10px;background:rgba(255,80,96,.12);color:#ffb1ba}.currencyAdmin__message--success{background:rgba(52,205,172,.12);color:#9cebd9}.currencyAdmin__selector{display:grid;grid-template-columns:minmax(240px,420px) auto;gap:12px;align-items:end}.currencyAdmin__field{display:grid;gap:7px;font-weight:800}.currencyAdmin__field select{border:1px solid rgba(255,255,255,.13);background:#0c1221;color:#fff;border-radius:9px;padding:10px}.currencyAdmin__featureSwitch{display:flex;align-items:flex-start;gap:11px;margin-top:15px;padding:13px 14px;border:1px solid rgba(255,255,255,.09);border-radius:12px;background:rgba(255,255,255,.035);font-weight:900}.currencyAdmin__featureSwitch input{width:20px;height:20px;margin-top:1px;accent-color:#35ceb0}.currencyAdmin__featureSwitch small{display:block;margin-top:3px;color:rgba(230,230,247,.55);font-weight:500}.currencyAdmin__currencyGrid{display:grid;grid-template-columns:repeat(auto-fit,minmax(210px,1fr));gap:10px;margin-top:16px}.currencyAdmin__currencyOption{display:flex;align-items:center;gap:10px;padding:11px 12px;border-radius:11px;border:1px solid rgba(255,255,255,.09);background:rgba(255,255,255,.035)}.currencyAdmin__currencyOption input{width:18px;height:18px;accent-color:#35ceb0}.currencyAdmin__currencyOption small{display:block;color:rgba(230,230,247,.55)}.currencyAdmin__locked{margin-left:auto;color:#efc47f;font-size:.7rem;font-weight:900;text-transform:uppercase}.currencyAdmin__summary{display:flex;gap:8px;flex-wrap:wrap;margin-top:12px}.currencyAdmin__chip{padding:5px 9px;border-radius:999px;background:rgba(74,151,216,.14);color:#a9d9ff;font-size:.76rem;font-weight:800}.currencyAdmin__modal{position:fixed;inset:0;z-index:1200;display:grid;place-items:center;background:rgba(0,0,0,.72)}.currencyAdmin__form{width:min(520px,calc(100vw - 28px));padding:22px;border-radius:18px;background:#11182b;border:1px solid rgba(255,255,255,.14);display:grid;gap:13px}.currencyAdmin__form label{display:grid;gap:6px;font-weight:700}.currencyAdmin__form input{border:1px solid rgba(255,255,255,.13);background:#0c1221;color:#fff;border-radius:9px;padding:10px}.currencyAdmin__formActions{display:flex;justify-content:flex-end;gap:9px;margin-top:5px}@media(max-width:720px){.currencyAdmin{padding:14px}.currencyAdmin__head,.currencyAdmin__cardHead{flex-direction:column}.currencyAdmin__selector{grid-template-columns:1fr}.currencyAdmin__tableWrap{overflow-x:auto}}
      `}</style>
      <div className="currencyAdmin">
        <div className="currencyAdmin__head">
          <div>
            <h1>Currency administration</h1>
            <p>Maintain the ISO catalog and decide which currencies each Business Unit may use.</p>
          </div>
          <button
            className="currencyAdmin__button currencyAdmin__button--primary"
            onClick={() => setDraft({ ...emptyDraft })}
          >
            <FiPlus />Add currency
          </button>
        </div>

        {error ? <div className="currencyAdmin__error">{error}</div> : null}

        <section className="currencyAdmin__card">
          <div className="currencyAdmin__cardHead">
            <div>
              <h2>Currencies allowed by Business Unit</h2>
              <p>
                Hotels only see currencies selected here. They decide which ones to activate and maintain their exchange rates.
              </p>
            </div>
          </div>
          <div className="currencyAdmin__selector">
            <label className="currencyAdmin__field">
              Business Unit
              <select
                value={selectedBusinessUnitId}
                onChange={(event) => setSelectedBusinessUnitId(event.target.value)}
                disabled={loading || assignmentLoading || assignmentSaving}
              >
                {!businessUnits.length && <option value="">No Business Units available</option>}
                {businessUnits.map((businessUnit) => (
                  <option key={businessUnit.id} value={businessUnit.id}>
                    {businessUnit.name} · #{businessUnit.id}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="currencyAdmin__button currencyAdmin__button--primary"
              onClick={saveAssignment}
              disabled={!selectedBusinessUnitId || assignmentLoading || assignmentSaving}
            >
              <FiSave />{assignmentSaving ? "Saving…" : "Save assignment"}
            </button>
          </div>
          <label className="currencyAdmin__featureSwitch">
            <input
              type="checkbox"
              checked={multiCurrencyEnabled}
              disabled={!selectedBusinessUnitId || assignmentLoading || assignmentSaving}
              onChange={(event) => {
                setMultiCurrencyEnabled(event.target.checked);
                setAssignmentMessage(null);
              }}
            />
            <span>
              Enable multi-currency for this Business Unit
              <small>
                Only administrators control this feature. The hotel maintains the enabled currencies and exchange rates.
              </small>
            </span>
          </label>

          {assignmentMessage ? (
            <div
              className={`currencyAdmin__message ${
                assignmentMessage.type === "success" ? "currencyAdmin__message--success" : ""
              }`}
              style={{ marginTop: 14 }}
            >
              {assignmentMessage.type === "success" ? <FiCheck style={{ marginRight: 7 }} /> : null}
              {assignmentMessage.text}
            </div>
          ) : null}

          {assignmentLoading ? (
            <p style={{ marginTop: 16 }}>Loading assignment…</p>
          ) : (
            <div className="currencyAdmin__currencyGrid">
              {activeCatalogRows.map((currency) => {
                const code = normalizeCode(currency.code);
                const enabledByHotel = enabledCodes.includes(code);
                const locked =
                  code === baseCurrencyCode ||
                  (Boolean(assignmentMeta?.multi_currency_operational) &&
                    multiCurrencyEnabled &&
                    enabledByHotel);
                return (
                  <label key={code} className="currencyAdmin__currencyOption">
                    <input
                      type="checkbox"
                      checked={allowedCodes.includes(code)}
                      disabled={locked || assignmentSaving}
                      onChange={() => toggleAllowedCurrency(code)}
                    />
                    <span>
                      <strong>{code}</strong>
                      <small>{currency.name} · {currency.minor_units ?? 2} decimals</small>
                    </span>
                    {locked ? (
                      <span className="currencyAdmin__locked">
                        {code === baseCurrencyCode ? "Base" : "Enabled"}
                      </span>
                    ) : null}
                  </label>
                );
              })}
            </div>
          )}

          {assignmentMeta ? (
            <div className="currencyAdmin__summary">
              <span className="currencyAdmin__chip">
                Admin control: {multiCurrencyEnabled ? "enabled" : "disabled"}
              </span>
              <span className="currencyAdmin__chip">
                PMS status: {assignmentMeta.multi_currency_operational ? "active" : "inactive"}
              </span>
              {baseCurrencyCode ? (
                <span className="currencyAdmin__chip">Base: {baseCurrencyCode}</span>
              ) : null}
              {enabledCodes.length ? (
                <span className="currencyAdmin__chip">Hotel selection: {enabledCodes.join(", ")}</span>
              ) : null}
            </div>
          ) : null}
        </section>

        <section className="currencyAdmin__card">
          <div className="currencyAdmin__cardHead">
            <div>
              <h2>ISO currency catalog</h2>
              <p>Inactive currencies cannot be assigned to any Business Unit.</p>
            </div>
          </div>
          {loading ? (
            <div>Loading…</div>
          ) : (
            <div className="currencyAdmin__tableWrap">
              <table className="currencyAdmin__table">
                <thead>
                  <tr><th>Code</th><th>Name</th><th>Symbol</th><th>Decimals</th><th>Order</th><th>Status</th><th /></tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td><strong>{row.code}</strong></td>
                      <td>{row.name}</td>
                      <td>{row.symbol || "—"}</td>
                      <td>{row.minor_units ?? 2}</td>
                      <td>{row.sort_order}</td>
                      <td>
                        <span className={`currencyAdmin__status ${row.is_active ? "" : "currencyAdmin__status--off"}`}>
                          {row.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <button className="currencyAdmin__button" onClick={() => setDraft({ ...row })}>
                          <FiEdit2 />Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {draft ? (
          <div className="currencyAdmin__modal" onMouseDown={() => !saving && setDraft(null)}>
            <div className="currencyAdmin__form" onMouseDown={(event) => event.stopPropagation()}>
              <h2>{draft.id ? "Edit currency" : "New currency"}</h2>
              <label>
                ISO code
                <input
                  maxLength={3}
                  value={draft.code}
                  disabled={Boolean(draft.id)}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, code: event.target.value.toUpperCase() }))
                  }
                />
              </label>
              <label>
                Name
                <input
                  value={draft.name}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, name: event.target.value }))
                  }
                />
              </label>
              <label>
                Symbol
                <input
                  maxLength={8}
                  value={draft.symbol || ""}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, symbol: event.target.value }))
                  }
                />
              </label>
              <label>
                Minor units (decimals)
                <input
                  type="number"
                  min="0"
                  max="6"
                  step="1"
                  disabled={Boolean(draft.id)}
                  value={draft.minor_units ?? 2}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, minor_units: event.target.value }))
                  }
                />
                {draft.id ? (
                  <small>Locked after creation so historical amounts cannot be reinterpreted.</small>
                ) : null}
              </label>
              <label>
                Sort order
                <input
                  type="number"
                  value={draft.sort_order}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, sort_order: Number(event.target.value) }))
                  }
                />
              </label>
              <label style={{ display: "flex", gridTemplateColumns: "auto 1fr", alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={draft.is_active !== false}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, is_active: event.target.checked }))
                  }
                />
                Active
              </label>
              <div className="currencyAdmin__formActions">
                <button className="currencyAdmin__button" onClick={() => setDraft(null)} disabled={saving}>
                  <FiX />Cancel
                </button>
                <button
                  className="currencyAdmin__button currencyAdmin__button--primary"
                  onClick={saveCatalogCurrency}
                  disabled={saving}
                >
                  <FiSave />{saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
